import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AcademicYearsService } from 'src/app/services/academic-years.service';
import { LanguageService } from 'src/app/services/language.service';

@Component({
  selector: 'app-academic-years',
  templateUrl: './academic-years.component.html',
  styleUrls: ['./academic-years.component.css']
})
export class AcademicYearsComponent {
  items: any[] = [];
  filteredItems: any[] = [];
  selected: any[] = [];
  searchTerm = '';
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [10, 20, 50];
  showDeleteModal = false;
  deleteModalBody = '';
  deleteModalParams: Record<string, unknown> = {};
  open = false;
  mode = 'add';
  selectedId = '';
  isError = false;
  errorMessage = '';
  form = new FormGroup({
    nameAr: new FormControl('', [Validators.required]),
    nameEn: new FormControl('', [Validators.required]),
    code: new FormControl('', [Validators.required]),
    startDate: new FormControl(''),
    endDate: new FormControl(''),
    isCurrent: new FormControl(false),
    active: new FormControl(true),
  });

  constructor(
    private academicYearsService: AcademicYearsService,
    private languageService: LanguageService,
  ) {}

  async ngOnInit() { await this.fetchItems(); }

  label(item: any): string {
    return this.languageService.current === 'ar' ? item.nameAr : item.nameEn;
  }

  async fetchItems() {
    this.items = await this.academicYearsService.list();
    this.applyFilter();
  }

  get paginatedItems() {
    const start = this.pageIndex * this.pageSize;
    return this.filteredItems.slice(start, start + this.pageSize);
  }
  get totalPages() { return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize)); }
  get pageStart() { return this.filteredItems.length ? this.pageIndex * this.pageSize + 1 : 0; }
  get pageEnd() { return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredItems.length); }

  applyFilter() {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredItems = term
      ? this.items.filter(item =>
          item.nameAr?.toLowerCase().includes(term) ||
          item.nameEn?.toLowerCase().includes(term) ||
          item.code?.toLowerCase().includes(term)
        )
      : [...this.items];
    this.pageIndex = 0;
  }

  onSearchChange() { this.applyFilter(); }
  onPageSizeChange(size: string) { this.pageSize = Number(size); this.pageIndex = 0; }
  previousPage() { if (this.pageIndex > 0) this.pageIndex--; }
  nextPage() { if (this.pageIndex < this.totalPages - 1) this.pageIndex++; }
  isSelected(item: any) { return this.selected.some(s => s.id === item.id); }
  toggleSelection(item: any, event: Event) {
    event.stopPropagation();
    this.selected = this.isSelected(item) ? this.selected.filter(s => s.id !== item.id) : [...this.selected, item];
  }
  clearSelection() { this.selected = []; }

  onAdd() {
    this.form.enable();
    this.mode = 'add';
    this.selectedId = '';
    this.form.reset({ nameAr: '', nameEn: '', code: '', startDate: '', endDate: '', isCurrent: false, active: true });
    this.isError = false;
    this.open = true;
  }

  onEdit() {
    this.openItem(this.selected[0], 'edit');
  }

  openRow(item: any) {
    this.openItem(item, 'view');
  }

  private openItem(item: any, mode: 'edit' | 'view') {
    this.form.enable();
    this.mode = mode;
    this.selectedId = item.id;
    this.form.patchValue({
      nameAr: item.nameAr,
      nameEn: item.nameEn,
      code: item.code,
      startDate: item.startDate || '',
      endDate: item.endDate || '',
      isCurrent: !!item.isCurrent,
      active: item.active !== false,
    });
    this.isError = false;
    this.open = true;
    if (mode === 'view') {
      this.form.disable();
    }
  }

  onDeleteMany() {
    this.deleteModalBody = this.selected.length === 1 ? 'ACADEMIC_YEARS.DELETE_ONE' : 'ACADEMIC_YEARS.DELETE_MANY';
    this.deleteModalParams = this.selected.length === 1
      ? { name: this.label(this.selected[0]) }
      : { count: this.selected.length };
    this.showDeleteModal = true;
  }

  async onDeleteConfirm(confirmed: boolean) {
    if (confirmed) {
      for (const item of this.selected) {
        try {
          await this.academicYearsService.delete(item.id);
        } catch (error: any) {
          this.isError = true;
          this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'ACADEMIC_YEARS.DELETE_FAILED';
          this.showDeleteModal = false;
          await this.fetchItems();
          return;
        }
      }
      this.selected = [];
      await this.fetchItems();
    }
    this.showDeleteModal = false;
  }

  async submit() {
    if (this.mode === 'view') {
      return;
    }
    if (this.form.invalid) {
      this.isError = true;
      this.errorMessage = 'COMMON.REQUIRED_FIELDS';
      return;
    }
    const raw = this.form.getRawValue();
    const body = {
      nameAr: raw.nameAr,
      nameEn: raw.nameEn,
      code: raw.code,
      startDate: raw.startDate || undefined,
      endDate: raw.endDate || undefined,
      isCurrent: !!raw.isCurrent,
      active: raw.active !== false,
    };
    try {
      if (this.mode === 'edit') {
        await this.academicYearsService.update(this.selectedId, body);
      } else {
        await this.academicYearsService.create(body);
      }
      this.open = false;
      this.selected = [];
      await this.fetchItems();
    } catch (error: any) {
      this.isError = true;
      this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'ACADEMIC_YEARS.SAVE_FAILED';
    }
  }

  closeModal() { this.open = false; this.isError = false; this.form.enable(); }
}
